
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || '';
/**
 * Secure Lab Integration Service
 * Uses backend API endpoints instead of direct Firestore access
 */

/**
 * Get all lab integrations with optional filtering
 * @param {Object} filters - Filters (facilityId, active)
 * @param {number} page - Page number
 * @param {number} limit - Items per page
 * @returns {Promise<Object>} Lab integrations with pagination
 */
export async function getLabIntegrations(filters = {}, page = 1, limit = 50) {
  try {
    console.log('Fetching lab integrations with filters:', filters);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/integrations/lab-integrations?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return {
      labIntegrations: response.data.labIntegrations,
      pagination: response.data.pagination
    };
  } catch (error) {
    console.error('Error fetching lab integrations:', error);
    throw error;
  }
}

/**
 * Get a specific lab integration by ID
 * @param {string} integrationId - Lab integration ID
 * @returns {Promise<Object>} Lab integration object
 */
export async function getLabIntegrationById(integrationId) {
  try {
    console.log('Fetching lab integration by ID:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/integrations/lab-integrations/${integrationId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.integration;
  } catch (error) {
    console.error('Error fetching lab integration by ID:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Create a new lab integration
 * @param {Object} integrationData - Lab integration data
 * @returns {Promise<Object>} Created lab integration
 */
export async function addLabIntegration(integrationData) {
  try {
    console.log('Creating lab integration:', integrationData.name);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/lab-integrations`,
      integrationData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error creating lab integration:', error);
    throw error;
  }
}

/**
 * Update an existing lab integration
 * @param {string} integrationId - Lab integration ID
 * @param {Object} updateData - Data to update
 * @returns {Promise<Object>} Update result
 */
export async function updateLabIntegration(integrationId, updateData) {
  try {
    console.log('Updating lab integration:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/integrations/lab-integrations/${integrationId}`,
      updateData,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error updating lab integration:', error);
    throw error;
  }
}

/**
 * Delete a lab integration
 * @param {string} integrationId - Lab integration ID
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteLabIntegration(integrationId) {
  try {
    console.log('Deleting lab integration:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.delete(
      `${API_BASE}/integrations/lab-integrations/${integrationId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error deleting lab integration:', error);
    throw error;
  }
}

/**
 * Get lab integrations for a specific facility
 * @param {string} facilityId - Facility ID
 * @param {boolean} activeOnly - Whether to include only active integrations
 * @returns {Promise<Array>} Array of lab integrations
 */
export async function getLabIntegrationsForFacility(facilityId, activeOnly = true) {
  try {
    const filters = {
      facilityId,
      active: activeOnly ? 'true' : undefined
    };
    
    const result = await getLabIntegrations(filters, 1, 100);
    return result.labIntegrations;
  } catch (error) {
    console.error('Error fetching lab integrations for facility:', error);
    throw error;
  }
}

/**
 * Test lab integration connectivity
 * @param {string} integrationId - Lab integration ID
 * @returns {Promise<Object>} Test result
 */
export async function testLabIntegration(integrationId) {
  try {
    console.log('Testing lab integration:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/test`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.testResult;
  } catch (error) {
    console.error('Error testing lab integration:', error);
    throw error;
  }
}

/**
 * Send lab order to integration
 * @param {string} integrationId - Lab integration ID
 * @param {Object} orderData - Lab order data
 * @returns {Promise<Object>} Send result
 */
export async function sendLabOrder(integrationId, orderData) {
  try {
    console.log('Sending lab order via integration:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/send-order`,
      { orderData },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error sending lab order:', error);
    throw error;
  }
}

/**
 * Poll for lab results from integration
 * @param {string} integrationId - Lab integration ID
 * @param {Object} pollOptions - Polling options (orderId, since)
 * @returns {Promise<Object>} Poll result with new results
 */
export async function pollLabResults(integrationId, pollOptions = {}) {
  try {
    console.log('Polling lab results from integration:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/poll-results`,
      pollOptions,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error polling lab results:', error);
    throw error;
  }
}

/**
 * Get lab integration status and statistics
 * @param {string} integrationId - Lab integration ID
 * @returns {Promise<Object>} Integration status and stats
 */
export async function getLabIntegrationStatus(integrationId) {
  try {
    console.log('Getting lab integration status:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/status`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.status;
  } catch (error) {
    console.error('Error getting lab integration status:', error);
    throw error;
  }
}

/**
 * Configure lab integration settings
 * @param {string} integrationId - Lab integration ID
 * @param {Object} settings - Integration settings
 * @returns {Promise<Object>} Configuration result
 */
export async function configureLabIntegration(integrationId, settings) {
  try {
    console.log('Configuring lab integration:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/configure`,
      { settings },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error configuring lab integration:', error);
    throw error;
  }
}

/**
 * Get lab integration logs
 * @param {string} integrationId - Lab integration ID
 * @param {Object} logOptions - Log options (level, startDate, endDate, limit)
 * @returns {Promise<Array>} Array of log entries
 */
export async function getLabIntegrationLogs(integrationId, logOptions = {}) {
  try {
    console.log('Getting lab integration logs:', integrationId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    Object.entries(logOptions).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/logs?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.logs;
  } catch (error) {
    console.error('Error getting lab integration logs:', error);
    throw error;
  }
}

/**
 * Sync lab integration data
 * @param {string} integrationId - Lab integration ID
 * @param {Object} syncOptions - Sync options (fullSync, entities)
 * @returns {Promise<Object>} Sync result
 */
export async function syncLabIntegrationData(integrationId, syncOptions = {}) {
  try {
    console.log('Syncing lab integration data:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/sync`,
      syncOptions,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error syncing lab integration data:', error);
    throw error;
  }
}

/**
 * Get lab test catalog from integration
 * @param {string} integrationId - Lab integration ID
 * @param {Object} catalogOptions - Catalog options (category, search)
 * @returns {Promise<Array>} Array of available lab tests
 */
export async function getLabTestCatalog(integrationId, catalogOptions = {}) {
  try {
    console.log('Getting lab test catalog from integration:', integrationId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    Object.entries(catalogOptions).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/catalog?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.tests;
  } catch (error) {
    console.error('Error getting lab test catalog:', error);
    throw error;
  }
}

/**
 * Validate lab integration credentials
 * @param {string} integrationId - Lab integration ID
 * @returns {Promise<Object>} Validation result
 */
export async function validateLabIntegrationCredentials(integrationId) {
  try {
    console.log('Validating lab integration credentials:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/validate-credentials`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error validating lab integration credentials:', error);
    throw error;
  }
}

// ===== NEW API FUNCTIONS =====

/**
 * Test connection to lab integration (real SFTP/API test)
 * @param {string} integrationId - Lab integration ID
 * @returns {Promise<Object>} Test result with responseTime, statusCode, and success status
 */
export async function testLabIntegrationConnection(integrationId) {
  try {
    console.log('Testing lab integration connection:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/test-connection`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000 // 30 second timeout for connection test
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error testing lab integration connection:', error);
    throw error;
  }
}

/**
 * Send lab order using integration router (supports HL7/FHIR via SFTP/API)
 * @param {string} integrationId - Lab integration ID
 * @param {Object} orderData - Extended order data with patient demographics
 * @returns {Promise<Object>} Send result with protocol, dataFormat, and status
 */
export async function sendLabOrderV2(integrationId, orderData) {
  try {
    console.log('Sending lab order v2 via integration:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/send-order-v2`,
      { orderData },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error sending lab order v2:', error);
    throw error;
  }
}

/**
 * Get transmission logs for an integration
 * @param {string} integrationId - Lab integration ID
 * @param {Object} options - Filter options (status, direction, startDate, endDate, limit)
 * @returns {Promise<Object>} Logs array with total count
 */
export async function getTransmissionLogs(integrationId, options = {}) {
  try {
    console.log('Getting transmission logs for integration:', integrationId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/transmission-logs?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting transmission logs:', error);
    throw error;
  }
}

/**
 * Get transmission statistics for an integration
 * @param {string} integrationId - Lab integration ID
 * @param {Object} options - Options (startDate, endDate)
 * @returns {Promise<Object>} Statistics with success rate
 */
export async function getTransmissionStats(integrationId, options = {}) {
  try {
    console.log('Getting transmission stats for integration:', integrationId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/transmission-stats?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting transmission stats:', error);
    throw error;
  }
}

/**
 * Get webhook info for an integration
 * @param {string} integrationId - Lab integration ID
 * @returns {Promise<Object>} Webhook URL and instructions
 */
export async function getWebhookInfo(integrationId) {
  try {
    console.log('Getting webhook info for integration:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/webhook-info`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error getting webhook info:', error);
    throw error;
  }
}

/**
 * Generate webhook secret for an integration
 * @param {string} integrationId - Lab integration ID
 * @returns {Promise<Object>} Generated webhook secret
 */
export async function generateWebhookSecret(integrationId) {
  try {
    console.log('Generating webhook secret for integration:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.post(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/generate-webhook-secret`,
      {},
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error generating webhook secret:', error);
    throw error;
  }
}

/**
 * Get lab integration usage statistics
 * @param {string} integrationId - Lab integration ID
 * @param {Object} statsOptions - Statistics options (startDate, endDate, groupBy)
 * @returns {Promise<Object>} Usage statistics
 */
export async function getLabIntegrationStats(integrationId, statsOptions = {}) {
  try {
    console.log('Getting lab integration statistics:', integrationId);
    const token = await getAuthToken();
    
    const params = new URLSearchParams();
    Object.entries(statsOptions).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value.toString());
      }
    });
    
    const response = await axios.get(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/stats?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.stats;
  } catch (error) {
    console.error('Error getting lab integration statistics:', error);
    throw error;
  }
}

// ===== SECURE CREDENTIAL MANAGEMENT =====

/**
 * Update credentials for a lab integration (stored securely in Secret Manager)
 * @param {string} integrationId - Lab integration ID
 * @param {Object} credentials - Credential data (password, privateKey, apiKey, etc.)
 * @returns {Promise<Object>} Update result
 */
export async function updateLabIntegrationCredentials(integrationId, credentials) {
  try {
    console.log('Updating lab integration credentials:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.put(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/credentials`,
      credentials,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error updating lab integration credentials:', error);
    throw error;
  }
}

/**
 * Delete/revoke credentials for a lab integration
 * @param {string} integrationId - Lab integration ID
 * @returns {Promise<Object>} Deletion result
 */
export async function deleteLabIntegrationCredentials(integrationId) {
  try {
    console.log('Deleting lab integration credentials:', integrationId);
    const token = await getAuthToken();
    
    const response = await axios.delete(
      `${API_BASE}/integrations/lab-integrations/${integrationId}/credentials`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error deleting lab integration credentials:', error);
    throw error;
  }
}

// Legacy function names for backward compatibility
export async function getAllFacilities() {
  console.warn('getAllFacilities is deprecated. Use facility service instead.');
  try {
    // Import facility service function
    const { getAllFacilities: getFacilities } = await import('./facilityService');
    return await getFacilities();
  } catch (error) {
    console.error('Error getting all facilities:', error);
    throw error;
  }
}
